import { OutlinedTextFieldProps, TextField } from '@mui/material';
import _debounce from 'lodash/debounce';
import React, { useEffect, useMemo, useRef } from 'react';

const ALLOWED_KEYS = ['Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Escape'];

type SearchInputProps = Omit<OutlinedTextFieldProps, 'onChange' | 'defaultValue' | 'value' | 'name' | 'variant'> & {
    name: string;
    defaultValue?: string;
    onSearch: (value: string, name: string) => void;
    isLoading?: boolean;
    debounceMs?: number;
    trimValue?: boolean;
};

export function SearchInput({
    name,
    defaultValue = '',
    onSearch,
    isLoading = false,
    debounceMs = 300,
    trimValue = true,
    size = 'small',
    className = 'w-100',
    inputProps,
    sx,
    ...rest
}: SearchInputProps) {
    const onSearchRef = useRef(onSearch);
    useEffect(() => {
        onSearchRef.current = onSearch;
    }, [onSearch]);

    const debouncedSearch = useMemo(
        () =>
            _debounce((value: string) => {
                onSearchRef.current(trimValue ? value.trim() : value, name);
            }, debounceMs),
        [debounceMs, name, trimValue]
    );

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (isLoading) {
            const isCopyOrSelectAll = (e.ctrlKey || e.metaKey) && ['a', 'c'].includes(e.key.toLowerCase());
            if (!ALLOWED_KEYS.includes(e.key) && !isCopyOrSelectAll) {
                e.preventDefault();
                return;
            }
        }
        inputProps?.onKeyDown?.(e);
    };

    const blockWhileLoading = (e: React.SyntheticEvent) => {
        if (isLoading) e.preventDefault();
    };

    return (
        <TextField
            {...rest}
            name={name}
            defaultValue={defaultValue}
            onChange={e => debouncedSearch(e.target.value)}
            variant="outlined"
            size={size}
            className={className}
            inputProps={{
                ...inputProps,
                onKeyDown: handleKeyDown,
                onPaste: blockWhileLoading,
                onCut: blockWhileLoading,
                onDrop: blockWhileLoading,
                'aria-busy': isLoading,
            }}
            sx={[
                {
                    '& .MuiInputBase-input': { transition: 'color 0.2s' },
                },
                isLoading && {
                    '& .MuiOutlinedInput-root:hover:not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                    '& .MuiInputBase-input': { color: 'text.disabled' },
                    '& .MuiInputLabel-root:not(.Mui-focused)': { color: 'text.disabled' },
                    '& .MuiInputBase-input::placeholder': { color: 'text.disabled', opacity: 1 },
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        />
    );
}
